import React from 'react';
import {
    Box,
    Flex,
    HStack,
    Icon,
    IconButton,
    Text,
    Tooltip,
    VStack,
    Input,
    Select,
    Editable,
    EditableInput,
    EditablePreview,
    Button,
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import type { IconType } from 'react-icons';
import {
    TbArrowBackUp,
    TbBulb,
    TbFileCheck,
    TbFlag,
    TbLink,
    TbReceiptRefund,
    TbSparkles,
    TbTruck,
    TbTruckReturn,
} from 'react-icons/tb';
import type { InvoiceDetailResponse, InvoiceType } from '../../types/invoice';
import DatePicker from '../DatePicker';
import type { ZoomImageHighlightBox } from '../ZoomImage';
import { useGetLocations } from '../../hooks/useGetLocations';
import { useGetInvoiceCcVendors } from '../../hooks/invoice/useGetInvoiceCcVendors';
import type { NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';
import { getInvoiceNexIdentifierUi } from '../../utils/invoiceNexIdentifierUi';
import { canRefreshNexsyisForInvoice, canRefreshPoNexsyisForInvoice, canRefreshRoNexsyisForInvoice, hasExplicitBothModePoRo } from '../../utils/invoiceDualIdentifiers';
import { formatBothModeNexLookupTooltip } from '../../utils/invoiceNexLookupTooltip';
import { getKeyIdentifiersVendorDisplay } from '../../utils/invoiceVendorDisplay';
import type { ExtractionLayoutField } from '../../hooks/invoice/useInvoiceExtractionLayout';
import type {
    KeyIdentifierCorrectionCommit,
    SubmittedIdentifierCorrectionRequest,
} from '../../utils/invoiceKeyIdentifierTeaching';

const ACCENT = '#0071E3';

const INVOICE_TYPE_CHIP: Record<
    InvoiceType,
    {
        label: string;
        icon: IconType;
        borderColor: string;
        bg: string;
        color: string;
    }
> = {
    received: {
        label: 'Received',
        icon: TbFileCheck,
        borderColor: 'blue.500',
        bg: 'blue.50',
        color: 'blue.700',
    },
    returned: {
        label: 'Credited',
        icon: TbReceiptRefund,
        borderColor: 'red.500',
        bg: 'red.100',
        color: 'red.800',
    },
    return_slip: {
        label: 'Return Slip',
        icon: TbTruckReturn,
        borderColor: 'yellow.600',
        bg: 'yellow.100',
        color: 'yellow.800',
    },
    sublet: {
        label: 'Sublet',
        icon: TbTruck,
        borderColor: 'purple.500',
        bg: 'purple.50',
        color: 'purple.700',
    },
};

const INVOICE_TYPE_ORDER: InvoiceType[] = ['received', 'returned', 'return_slip', 'sublet'];

function KeyIdentifierFieldTitle({
    label,
    teachField,
    teachableFields,
    onOpenExtractionTeaching,
    onReportWrongValue,
}: {
    label: string;
    teachField?: ExtractionLayoutField;
    teachableFields?: ExtractionLayoutField[];
    onOpenExtractionTeaching?: () => void;
    /** Report the printed value so it becomes an AI hint. */
    onReportWrongValue?: () => void;
}) {
    const canTeach =
        !!teachField &&
        !!onOpenExtractionTeaching &&
        !!teachableFields?.includes(teachField);

    return (
        <HStack spacing={1} mb={2} align="center" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="bold" color="gray.800">
                {label}
            </Text>
            {onReportWrongValue ? (
                <Button
                    variant="link"
                    size="xs"
                    color={ACCENT}
                    fontWeight="medium"
                    opacity={0}
                    pointerEvents="none"
                    minW="auto"
                    h="auto"
                    p={0.5}
                    whiteSpace="nowrap"
                    _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                    _hover={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onReportWrongValue();
                    }}
                >
                    Did we get this wrong?
                </Button>
            ) : null}
            {canTeach ? (
                <Tooltip label="Teach extraction for this field" placement="top" hasArrow>
                    <IconButton
                        aria-label={`Teach extraction for ${label}`}
                        icon={<Icon as={TbSparkles as React.ElementType} boxSize={3.5} />}
                        size="xs"
                        variant="ghost"
                        color={ACCENT}
                        opacity={0}
                        pointerEvents="none"
                        minW="auto"
                        h="auto"
                        p={0.5}
                        _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                        _hover={{ bg: 'blue.50' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenExtractionTeaching!();
                        }}
                    />
                </Tooltip>
            ) : null}
        </HStack>
    );
}

function KeyIdentifierReviewFlag({
    ariaLabel,
    items,
}: {
    ariaLabel: string;
    items: Array<{ title: string; detail?: string }>;
}) {
    if (items.length === 0) return null;
    return (
        <Tooltip
            label={
                <VStack align="start" spacing={2}>
                    {items.map((item) => (
                        <VStack key={item.title} align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="semibold">{item.title}</Text>
                            {item.detail ? <Text fontSize="xs">{item.detail}</Text> : null}
                        </VStack>
                    ))}
                </VStack>
            }
            placement="top"
            hasArrow
        >
            <IconButton
                aria-label={ariaLabel}
                icon={<Icon as={TbFlag as React.ElementType} boxSize={4} />}
                variant="ghost"
                size="xs"
                color="orange.500"
                _hover={{ bg: 'transparent' }}
                minW="auto"
                h="auto"
                p={0}
            />
        </Tooltip>
    );
}

function formatInvoiceDateForKeyIds(d: Date): string {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
}

function extractNexVendorNamesFromResponse(nexResponse: unknown): string[] {
    if (!Array.isArray(nexResponse)) return [];
    return Array.from(
        new Set(
            nexResponse
                .map((po) => {
                    if (!po || typeof po !== 'object') return '';
                    const vendorName = (po as { vendorName?: unknown }).vendorName;
                    return typeof vendorName === 'string' ? vendorName.trim() : '';
                })
                .filter((name) => name.length > 0)
        )
    );
}

export interface InvoiceKeyIdentifiersSectionProps {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    isEditingInvoiceNumber: boolean;
    setIsEditingInvoiceNumber: (v: boolean) => void;
    invoiceInputRef: React.RefObject<HTMLInputElement | null>;
    getStringValue: (field: keyof InvoiceDetailResponse) => string;
    getDateValue: () => Date;
    isFieldEdited: (field: keyof InvoiceDetailResponse) => boolean;
    updateField: (field: keyof InvoiceDetailResponse, value: any) => void;
    openRevertModal: (fieldKey: string, fieldLabel: string) => void;
    openReconciliationDetailModal: (modal: any) => void;
    isReadOnly: boolean;
    /** When set (editable invoices), user can pick another type; parent shows confirm and saves. */
    onInvoiceTypeChangeRequest?: (next: InvoiceType) => void;
    /** When set (editable invoices), user can pick another shop location; parent shows confirm and saves. */
    onShopLocationChangeRequest?: (next: { locationId: string; locationTitle: string }) => void;
    /** When true, omit the section heading (e.g. parent provides a foldable title). */
    hideTitle?: boolean;
    /** Re-fetch NEX for the identifier in the field and re-run reconciliation (flagged / editable invoices). */
    onRefreshNexsyisData?: (strategy?: 'po_first' | 'ro_only') => void;
    isRefreshNexsyisPending?: boolean;
    onFieldImageHighlightChange?: (region: ZoomImageHighlightBox | null) => void;
    /** Org NEX reference mode (Settings → Invoicing); drives RO vs PO vs PO/RO labels. */
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    /** Open the AI extraction hints modal. */
    onOpenExtractionTeaching?: () => void;
    /** Fields that appear in the teach-extraction table for this invoice. */
    teachableFields?: ExtractionLayoutField[];
    /** After the user commits a corrected identifier value, offer to teach extraction. */
    onKeyIdentifierCorrectionCommitted?: (commit: KeyIdentifierCorrectionCommit) => void;
    /** Open the correction dialog so the printed value can become an AI hint. */
    onReportSubmittedIdentifier?: (request: SubmittedIdentifierCorrectionRequest) => void;
}

export const InvoiceKeyIdentifiersSection: React.FC<InvoiceKeyIdentifiersSectionProps> = ({
    invoice,
    editedFields,
    isEditingInvoiceNumber,
    setIsEditingInvoiceNumber,
    invoiceInputRef,
    getStringValue,
    getDateValue,
    isFieldEdited,
    updateField,
    openRevertModal,
    openReconciliationDetailModal,
    isReadOnly,
    onInvoiceTypeChangeRequest,
    onShopLocationChangeRequest,
    hideTitle = false,
    onRefreshNexsyisData,
    isRefreshNexsyisPending = false,
    onFieldImageHighlightChange,
    nexReconciliationIdentifierMode = 'ro',
    onOpenExtractionTeaching,
    teachableFields,
    onKeyIdentifierCorrectionCommitted,
    onReportSubmittedIdentifier,
}) => {
    const idUi = React.useMemo(
        () => getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode),
        [nexReconciliationIdentifierMode]
    );
    const currentInvoiceType = (editedFields.invoiceType ?? invoice?.invoiceType ?? 'received') as InvoiceType;
    const showCcVendor = currentInvoiceType === 'received' || currentInvoiceType === 'sublet';
    const { data: ccVendors = [], isLoading: isLoadingCcVendors } = useGetInvoiceCcVendors(
        showCcVendor && !isReadOnly
    );
    const selectedCcVendorId =
        editedFields.ccVendorId !== undefined ? editedFields.ccVendorId : invoice?.ccVendorId;
    const selectedCcVendorName =
        editedFields.ccVendorName !== undefined ? editedFields.ccVendorName : invoice?.ccVendorName;
    const showBothModeSplitFields = nexReconciliationIdentifierMode === 'both';
    const showExplicitBothPoRo = React.useMemo(
        () =>
            showBothModeSplitFields ||
            hasExplicitBothModePoRo({
                mode: nexReconciliationIdentifierMode,
                purchaseOrderNumber: String(editedFields.purchaseOrderNumber ?? invoice?.purchaseOrderNumber ?? ''),
                shopRoNumber: String(editedFields.shopRoNumber ?? invoice?.shopRoNumber ?? ''),
            }),
        [
            showBothModeSplitFields,
            nexReconciliationIdentifierMode,
            editedFields.purchaseOrderNumber,
            editedFields.shopRoNumber,
            invoice?.purchaseOrderNumber,
            invoice?.shopRoNumber,
        ],
    );
    const nexLookupTooltip = React.useMemo(
        () =>
            showBothModeSplitFields
                ? formatBothModeNexLookupTooltip({
                      matchedVia: invoice?.nexReconciliationMatchedVia,
                      matchedCandidate: invoice?.nexReconciliationMatchedCandidate,
                      shopRoNumber: getStringValue('shopRoNumber'),
                  })
                : null,
        [
            showBothModeSplitFields,
            invoice?.nexReconciliationMatchedVia,
            invoice?.nexReconciliationMatchedCandidate,
            getStringValue,
        ],
    );
    const canRefreshNex = React.useMemo(
        () =>
            canRefreshNexsyisForInvoice({
                roNumber: String(editedFields.roNumber ?? invoice?.roNumber ?? ''),
                purchaseOrderNumber: String(editedFields.purchaseOrderNumber ?? invoice?.purchaseOrderNumber ?? ''),
                shopRoNumber: String(editedFields.shopRoNumber ?? invoice?.shopRoNumber ?? ''),
            }),
        [
            editedFields.roNumber,
            editedFields.purchaseOrderNumber,
            editedFields.shopRoNumber,
            invoice?.roNumber,
            invoice?.purchaseOrderNumber,
            invoice?.shopRoNumber,
        ],
    );
    const identifierFields = React.useMemo(
        () => ({
            mode: nexReconciliationIdentifierMode,
            roNumber: String(editedFields.roNumber ?? invoice?.roNumber ?? ''),
            purchaseOrderNumber: String(editedFields.purchaseOrderNumber ?? invoice?.purchaseOrderNumber ?? ''),
            shopRoNumber: String(editedFields.shopRoNumber ?? invoice?.shopRoNumber ?? ''),
        }),
        [
            nexReconciliationIdentifierMode,
            editedFields.roNumber,
            editedFields.purchaseOrderNumber,
            editedFields.shopRoNumber,
            invoice?.roNumber,
            invoice?.purchaseOrderNumber,
            invoice?.shopRoNumber,
        ],
    );
    const canRefreshPoNex = React.useMemo(
        () => canRefreshPoNexsyisForInvoice(identifierFields),
        [identifierFields],
    );
    const canRefreshRoNex = React.useMemo(
        () => canRefreshRoNexsyisForInvoice(identifierFields),
        [identifierFields],
    );
    const vendorMismatch = invoice?.reconciliationIssues?.vendorNameMismatch;
    const staleNexFolder = invoice?.reconciliationIssues?.staleNexFolder;
    const shopMismatch = invoice?.reconciliationIssues?.uploaderLocationMismatch;
    const isSubmittedOrManual =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';
    const highlightStaleRo = !!staleNexFolder && !isSubmittedOrManual;
    const highlightShopMismatch = !!shopMismatch && !isSubmittedOrManual;
    const primaryIdentifierFlagItems: Array<{ title: string; detail?: string }> = [];
    if (invoice?.reconciliationIssues?.roNumberNotFound) {
        primaryIdentifierFlagItems.push({
            title: idUi.notFoundTooltipTitle,
            detail: idUi.notFoundTooltipValueLine(
                getStringValue(showExplicitBothPoRo ? 'purchaseOrderNumber' : 'roNumber').trim(),
            ),
        });
    }
    if (highlightStaleRo && !showExplicitBothPoRo && nexReconciliationIdentifierMode !== 'po') {
        primaryIdentifierFlagItems.push({
            title: 'Stale folder',
            detail: staleNexFolder?.message,
        });
    }
    const shopRoFlagItems: Array<{ title: string; detail?: string }> = highlightStaleRo
        ? [{ title: 'Stale folder', detail: staleNexFolder?.message }]
        : [];
    const shopLocationFlagItems: Array<{ title: string; detail?: string }> = highlightShopMismatch
        ? [{ title: 'Shop mismatch', detail: shopMismatch?.message }]
        : [];
    const fallbackNexVendorNames = extractNexVendorNamesFromResponse(invoice?.nexResponse);
    const nexVendorNamesForSelector = Array.from(
        new Set(
            [
                ...(vendorMismatch?.nexVendorNames ?? []),
                ...(vendorMismatch?.nexVendorName ? [vendorMismatch.nexVendorName] : []),
                ...fallbackNexVendorNames,
            ]
                .map((name) => String(name).trim())
                .filter((name) => name.length > 0)
        )
    );
    const supplierForCompare = getStringValue('supplierText');
    const keyIdentifiersVendorDisplay = getKeyIdentifiersVendorDisplay({
        invoice,
        supplierText: supplierForCompare,
        vendorMismatch,
    });

    const { data: locationsData } = useGetLocations();
    const shopLocationOptions = React.useMemo(() => {
        const rows = (locationsData?.locations ?? []).filter((l) => l._id !== 'all');
        const currentId = String(editedFields.locationId ?? invoice?.locationId ?? '').trim();
        const currentTitle = String(editedFields.locationTitle ?? invoice?.locationTitle ?? '').trim();
        if (currentId && !rows.some((l) => String(l._id) === currentId)) {
            return [...rows, { _id: currentId, title: currentTitle || 'Current location' }];
        }
        return rows;
    }, [locationsData, editedFields.locationId, editedFields.locationTitle, invoice?.locationId, invoice?.locationTitle]);

    const shopLocationSelectValue = String(editedFields.locationId ?? invoice?.locationId ?? '').trim();

    const reportWrongValue = (
        sourceField: SubmittedIdentifierCorrectionRequest['sourceField'],
        observationField: ExtractionLayoutField,
        fieldLabel: string,
        submittedValue: string,
    ): (() => void) | undefined => {
        if (!onReportSubmittedIdentifier) return undefined;
        if (!teachableFields?.includes(observationField)) return undefined;
        return () => onReportSubmittedIdentifier({ sourceField, fieldLabel, submittedValue });
    };

    const commitKeyIdentifierCorrection = (
        commit: KeyIdentifierCorrectionCommit,
    ) => {
        if (!onKeyIdentifierCorrectionCommitted || isReadOnly) return;
        if (!commit.correctedValue.trim()) return;
        onKeyIdentifierCorrectionCommitted(commit);
    };

    const editableInputCommitOnBlur = (
        commitBase: Omit<KeyIdentifierCorrectionCommit, 'correctedValue'>,
    ) => ({
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            commitKeyIdentifierCorrection({
                ...commitBase,
                correctedValue: e.currentTarget.value,
            });
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.currentTarget.blur();
            }
        },
    });
    const shopLocationDisplay =
        getStringValue('locationTitle').trim() || getStringValue('locationId').trim() || '—';

    const openVendorSelectorModal = () => {
        openReconciliationDetailModal({
            type: 'flag',
            issue: {
                issueType: 'vendor_mismatch',
                hasMismatch: !!vendorMismatch,
                invoiceVendorName: getStringValue('supplierText') || vendorMismatch?.invoiceVendorName || null,
                nexVendorNames: nexVendorNamesForSelector,
                nexVendorName: vendorMismatch?.nexVendorName ?? undefined,
                reason: vendorMismatch?.reason,
            },
        });
    };

    return (
        <Box>
            {!hideTitle && (
                <Text color="gray.900" fontSize="sm" fontWeight="bold" mb={3}>
                    Key Identifiers
                </Text>
            )}
            <VStack align="stretch" spacing={0} overflow="hidden" bg="white">
                <Box p={4} borderBottom="1px solid" borderColor="gray.200">
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={4}>
                        <Box flex={1}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={2}>Invoice Type</Text>
                            <HStack spacing={1.5} flexWrap="wrap" align="center">
                                {INVOICE_TYPE_ORDER.map((t) => {
                                    const chip = INVOICE_TYPE_CHIP[t];
                                    const selected = currentInvoiceType === t;
                                    return (
                                        <Box
                                            key={t}
                                            as="button"
                                            type="button"
                                            flexShrink={0}
                                            whiteSpace="nowrap"
                                            px={2}
                                            py={1.5}
                                            borderRadius="md"
                                            border="2px solid"
                                            borderColor={selected ? chip.borderColor : 'gray.300'}
                                            bg={selected ? chip.bg : 'white'}
                                            color={selected ? chip.color : 'gray.600'}
                                            fontWeight={selected ? 'semibold' : 'medium'}
                                            fontSize="xs"
                                            _hover={{ borderColor: selected ? chip.borderColor : 'gray.400' }}
                                            onClick={() => {
                                                if (isReadOnly || selected || !onInvoiceTypeChangeRequest) return;
                                                onInvoiceTypeChangeRequest(t);
                                            }}
                                            disabled={isReadOnly || !onInvoiceTypeChangeRequest}
                                        >
                                            <HStack spacing={1.5} whiteSpace="nowrap">
                                                <Icon as={chip.icon as React.ElementType} boxSize={3.5} flexShrink={0} />
                                                <Text fontSize="xs" whiteSpace="nowrap">{chip.label}</Text>
                                            </HStack>
                                        </Box>
                                    );
                                })}
                                {!isReadOnly &&
                                    editedFields.invoiceType !== undefined &&
                                    editedFields.invoiceType !== (invoice?.invoiceType ?? 'received') && (
                                        <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Compare"
                                                icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => openRevertModal('invoiceType', 'Invoice type')}
                                                _hover={{ bg: 'gray.100' }}
                                                minW="auto"
                                                h="auto"
                                                p={1}
                                            />
                                        </Tooltip>
                                    )}
                            </HStack>
                        </Box>

                        {showCcVendor && (
                            <Box flex={1} minW={{ base: '100%', md: '220px' }}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={2}>
                                    Credit Card
                                </Text>
                                <HStack spacing={2} align="center">
                                    {isReadOnly ? (
                                        <Text fontSize="sm" color="gray.900">
                                            {selectedCcVendorName ||
                                                (selectedCcVendorId != null
                                                    ? `Vendor #${selectedCcVendorId}`
                                                    : 'No credit card')}
                                        </Text>
                                    ) : (
                                        <>
                                            <Select
                                                size="sm"
                                                maxW="100%"
                                                borderRadius="md"
                                                value={
                                                    selectedCcVendorId != null
                                                        ? String(selectedCcVendorId)
                                                        : ''
                                                }
                                                isDisabled={isLoadingCcVendors}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (!raw) {
                                                        updateField('ccVendorId', null);
                                                        updateField('ccVendorName', null);
                                                        return;
                                                    }
                                                    const id = Number(raw);
                                                    const match = ccVendors.find((v) => v.vendorID === id);
                                                    updateField('ccVendorId', id);
                                                    updateField(
                                                        'ccVendorName',
                                                        match?.name ?? match?.searchName ?? null
                                                    );
                                                }}
                                            >
                                                <option value="">No credit card</option>
                                                {ccVendors.map((v) => (
                                                    <option key={v.vendorID} value={String(v.vendorID)}>
                                                        {v.name || v.searchName}
                                                    </option>
                                                ))}
                                                {selectedCcVendorId != null &&
                                                    !ccVendors.some((v) => v.vendorID === selectedCcVendorId) && (
                                                        <option value={String(selectedCcVendorId)}>
                                                            {selectedCcVendorName ||
                                                                `Vendor #${selectedCcVendorId}`}
                                                        </option>
                                                    )}
                                            </Select>
                                            {isFieldEdited('ccVendorId') && (
                                                <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                                    <IconButton
                                                        aria-label="Compare"
                                                        icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => openRevertModal('ccVendorId', 'Credit card')}
                                                        _hover={{ bg: 'gray.100' }}
                                                        minW="auto"
                                                        h="auto"
                                                        p={1}
                                                    />
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </HStack>
                            </Box>
                        )}

                        <Box
                            flex={1}
                            minW={0}
                            bg={highlightShopMismatch ? 'orange.50' : 'transparent'}
                            borderRadius="md"
                        >
                            <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={2}>
                                Shop Location
                            </Text>
                            <Box px={0} py={0}>
                                <HStack spacing={2} flexWrap="wrap" align="center">
                                    <KeyIdentifierReviewFlag
                                        ariaLabel="Shop location mismatch"
                                        items={shopLocationFlagItems}
                                    />
                                    {isReadOnly ? (
                                        <Text fontSize="sm" color="gray.900">{shopLocationDisplay}</Text>
                                    ) : (
                                        <>
                                            <Select
                                                size="sm"
                                                maxW="100%"
                                                borderRadius="md"
                                                value={shopLocationSelectValue}
                                                isDisabled={shopLocationOptions.length === 0}
                                                placeholder={shopLocationOptions.length === 0 ? 'No locations' : 'Select location'}
                                                onChange={(e) => {
                                                    const selectedId = e.target.value;
                                                    const row = shopLocationOptions.find((l) => String(l._id) === selectedId);
                                                    if (!selectedId || selectedId === shopLocationSelectValue) return;
                                                    if (onShopLocationChangeRequest) {
                                                        onShopLocationChangeRequest({
                                                            locationId: selectedId,
                                                            locationTitle: row?.title ?? '',
                                                        });
                                                        return;
                                                    }
                                                    updateField('locationId', selectedId);
                                                    updateField('locationTitle', row?.title ?? '');
                                                }}
                                            >
                                                {shopLocationOptions.map((loc) => (
                                                    <option key={String(loc._id)} value={String(loc._id)}>
                                                        {loc.title}
                                                    </option>
                                                ))}
                                            </Select>
                                            {isFieldEdited('locationId') && (
                                                <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                                    <IconButton
                                                        aria-label="Compare"
                                                        icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => openRevertModal('locationId', 'Shop location')}
                                                        _hover={{ bg: 'gray.100' }}
                                                        minW="auto"
                                                        h="auto"
                                                        p={1}
                                                    />
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </HStack>
                            </Box>
                        </Box>
                    </Flex>
                </Box>

                <Box p={4} borderBottom="1px solid" borderColor="gray.200">
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={4}>
                        {!showExplicitBothPoRo && (
                        <Box
                            flex={1}
                            role="group"
                            bg={primaryIdentifierFlagItems.length > 0 ? 'orange.50' : 'transparent'}
                            borderRadius="md"
                            onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.roNumber ?? null)}
                            onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                        >
                            <KeyIdentifierFieldTitle
                                label={idUi.keyIdentifiersFieldTitle}
                                teachField="roNumber"
                                teachableFields={teachableFields}
                                onOpenExtractionTeaching={onOpenExtractionTeaching}
                                onReportWrongValue={reportWrongValue(
                                    'roNumber',
                                    'roNumber',
                                    idUi.keyIdentifiersFieldTitle,
                                    getStringValue('roNumber'),
                                )}
                            />
                            <Box px={0} py={0}>
                                <HStack spacing={2} flexWrap="wrap" align="center">
                                    <KeyIdentifierReviewFlag
                                        ariaLabel={idUi.notFoundFlagAriaLabel}
                                        items={primaryIdentifierFlagItems}
                                    />
                                    <Editable
                                        value={getStringValue('roNumber')}
                                        onChange={(value) => updateField('roNumber', value)}
                                        submitOnBlur
                                        placeholder={idUi.fieldPlaceholder}
                                        isDisabled={isReadOnly}
                                        w="180px"
                                        maxW="100%"
                                    >
                                        <EditablePreview w="100%" cursor="pointer" color="gray.900" fontSize="sm" py={0} px={0} />
                                        <EditableInput
                                            {...editableInputCommitOnBlur({
                                                invoiceField: 'roNumber',
                                                teachField: 'roNumber',
                                                fieldLabel: idUi.keyIdentifiersFieldTitle,
                                            })}
                                            border="none"
                                            outline="1.5px solid"
                                            outlineColor="blue.300"
                                            borderRadius="md"
                                            color="gray.900"
                                            fontSize="sm"
                                            bg="blue.50"
                                            py="2px"
                                            px={1}
                                            h="auto"
                                            lineHeight="short"
                                            _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                                        />
                                    </Editable>
                                    {!isReadOnly && typeof onRefreshNexsyisData === 'function' && (
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="blue"
                                            onClick={() => onRefreshNexsyisData()}
                                            isLoading={isRefreshNexsyisPending}
                                            loadingText="Refreshing..."
                                            isDisabled={!canRefreshNex || isRefreshNexsyisPending}
                                            px={2}
                                        >
                                            Refresh Nexsyis Data
                                        </Button>
                                    )}
                                    {!isReadOnly && isFieldEdited('roNumber') && (
                                        <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Compare"
                                                icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => openRevertModal('roNumber', idUi.revertFieldLabel)}
                                                _hover={{ bg: 'gray.100' }}
                                                minW="auto"
                                                h="auto"
                                                p={1}
                                            />
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Box>
                        </Box>
                        )}

                        {showExplicitBothPoRo && (
                            <Box
                                flex={1}
                                role="group"
                                bg={primaryIdentifierFlagItems.length > 0 ? 'orange.50' : 'transparent'}
                                borderRadius="md"
                                onMouseEnter={() =>
                                    onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.purchaseOrderNumber ?? null)
                                }
                                onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                            >
                                <KeyIdentifierFieldTitle
                                    label="PO Number"
                                    teachField="purchaseOrderNumber"
                                    teachableFields={teachableFields}
                                    onOpenExtractionTeaching={onOpenExtractionTeaching}
                                    onReportWrongValue={reportWrongValue(
                                        'purchaseOrderNumber',
                                        'purchaseOrderNumber',
                                        'PO Number',
                                        getStringValue('purchaseOrderNumber'),
                                    )}
                                />
                                <Box px={0} py={0}>
                                    <HStack spacing={2} flexWrap="wrap" align="center">
                                        <KeyIdentifierReviewFlag
                                            ariaLabel={idUi.notFoundFlagAriaLabel}
                                            items={primaryIdentifierFlagItems}
                                        />
                                        <Tooltip
                                            label={
                                                nexLookupTooltip ??
                                                (showBothModeSplitFields
                                                    ? 'Purchase order used in the Nexsyis lookup (first valid PO wins). Line items match from the repair order.'
                                                    : 'Purchase order from the invoice or Nexsyis.')
                                            }
                                            placement="top"
                                            hasArrow
                                            maxW="320px"
                                        >
                                            <Box flexShrink={0}>
                                                <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.400" cursor="help" />
                                            </Box>
                                        </Tooltip>
                                        <Editable
                                            value={getStringValue('purchaseOrderNumber')}
                                            onChange={(value) => updateField('purchaseOrderNumber', value)}
                                            submitOnBlur
                                            placeholder="PO Number"
                                            isDisabled={isReadOnly}
                                            w="180px"
                                            maxW="100%"
                                        >
                                            <EditablePreview w="100%" cursor="pointer" color="gray.900" fontSize="sm" py={0} px={0} />
                                            <EditableInput
                                                {...editableInputCommitOnBlur({
                                                    invoiceField: 'purchaseOrderNumber',
                                                    teachField: 'purchaseOrderNumber',
                                                    fieldLabel: 'PO Number',
                                                })}
                                                border="none"
                                                outline="1.5px solid"
                                                outlineColor="blue.300"
                                                borderRadius="md"
                                                color="gray.900"
                                                fontSize="sm"
                                                bg="blue.50"
                                                py="2px"
                                                px={1}
                                                h="auto"
                                                lineHeight="short"
                                                _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                                            />
                                        </Editable>
                                        {!isReadOnly && typeof onRefreshNexsyisData === 'function' && (
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="blue"
                                                onClick={() => onRefreshNexsyisData('po_first')}
                                                isLoading={isRefreshNexsyisPending}
                                                loadingText="Refreshing..."
                                                isDisabled={!canRefreshPoNex || isRefreshNexsyisPending}
                                                px={2}
                                            >
                                                Refresh Nexsyis Data
                                            </Button>
                                        )}
                                        {!isReadOnly && isFieldEdited('purchaseOrderNumber') && (
                                            <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                                <IconButton
                                                    aria-label="Compare"
                                                    icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => openRevertModal('purchaseOrderNumber', 'PO number')}
                                                    _hover={{ bg: 'gray.100' }}
                                                    minW="auto"
                                                    h="auto"
                                                    p={1}
                                                />
                                            </Tooltip>
                                        )}
                                    </HStack>
                                </Box>
                            </Box>
                        )}

                        {(nexReconciliationIdentifierMode === 'po' || showExplicitBothPoRo) && (
                            <Box
                                flex={1}
                                role="group"
                                bg={shopRoFlagItems.length > 0 ? 'orange.50' : 'transparent'}
                                borderRadius="md"
                                onMouseEnter={() =>
                                    onFieldImageHighlightChange?.(
                                        invoice.imageFieldHighlights?.shopRoNumber ??
                                            invoice.imageFieldHighlights?.roNumber ??
                                            null,
                                    )
                                }
                                onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                            >
                                <KeyIdentifierFieldTitle
                                    label="RO Number"
                                    teachField="roNumber"
                                    teachableFields={teachableFields}
                                    onOpenExtractionTeaching={onOpenExtractionTeaching}
                                    onReportWrongValue={reportWrongValue(
                                        'shopRoNumber',
                                        'shopRoNumber',
                                        'RO Number',
                                        getStringValue('shopRoNumber'),
                                    )}
                                />
                                <Box px={0} py={0}>
                                    <HStack spacing={2} flexWrap="wrap" align="center">
                                        <KeyIdentifierReviewFlag
                                            ariaLabel="Stale RO folder"
                                            items={shopRoFlagItems}
                                        />
                                        <Tooltip
                                            label={
                                                nexLookupTooltip ??
                                                (showBothModeSplitFields
                                                    ? 'Shop repair order (folder). Line items are matched from the Nexsyis repair-order endpoint.'
                                                    : showExplicitBothPoRo
                                                      ? 'Shop repair order from the invoice or Nexsyis (after PO or RO lookup).'
                                                      : 'Shop repair order folder for this PO. Taken from the invoice when present, otherwise auto-filled from Nexsyis after the PO is loaded.')
                                            }
                                            placement="top"
                                            hasArrow
                                            maxW="320px"
                                        >
                                            <Box flexShrink={0}>
                                                <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.400" cursor="help" />
                                            </Box>
                                        </Tooltip>
                                        <Editable
                                            value={getStringValue('shopRoNumber')}
                                            onChange={(value) => updateField('shopRoNumber', value)}
                                            submitOnBlur
                                            placeholder="RO Number"
                                            isDisabled={isReadOnly}
                                            flex={1}
                                        >
                                            <EditablePreview w="100%" cursor="pointer" color="gray.900" fontSize="sm" py={0} px={0} />
                                            <EditableInput
                                                {...editableInputCommitOnBlur({
                                                    invoiceField: 'shopRoNumber',
                                                    teachField: 'roNumber',
                                                    fieldLabel: 'RO number',
                                                })}
                                                border="none"
                                                outline="1.5px solid"
                                                outlineColor="blue.300"
                                                borderRadius="md"
                                                color="gray.900"
                                                fontSize="sm"
                                                bg="blue.50"
                                                py="2px"
                                                px={1}
                                                h="auto"
                                                lineHeight="short"
                                                _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                                            />
                                        </Editable>
                                        {!isReadOnly && showBothModeSplitFields && typeof onRefreshNexsyisData === 'function' && (
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="blue"
                                                onClick={() => onRefreshNexsyisData('ro_only')}
                                                isLoading={isRefreshNexsyisPending}
                                                loadingText="Refreshing..."
                                                isDisabled={!canRefreshRoNex || isRefreshNexsyisPending}
                                                px={2}
                                            >
                                                Refresh Nexsyis Data
                                            </Button>
                                        )}
                                        {!isReadOnly && isFieldEdited('shopRoNumber') && (
                                            <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                                <IconButton
                                                    aria-label="Compare"
                                                    icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => openRevertModal('shopRoNumber', 'RO number')}
                                                    _hover={{ bg: 'gray.100' }}
                                                    minW="auto"
                                                    h="auto"
                                                    p={1}
                                                />
                                            </Tooltip>
                                        )}
                                    </HStack>
                                </Box>
                            </Box>
                        )}

                        <Box
                            flex={1}
                            role="group"
                            onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.invoiceNumber ?? null)}
                            onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                        >
                            <KeyIdentifierFieldTitle
                                label="Invoice Number"
                                teachField="invoiceNumber"
                                teachableFields={teachableFields}
                                onOpenExtractionTeaching={onOpenExtractionTeaching}
                                onReportWrongValue={reportWrongValue(
                                    'invoiceNumber',
                                    'invoiceNumber',
                                    'Invoice number',
                                    getStringValue('invoiceNumber'),
                                )}
                            />
                            <Box px={0} py={0}>
                                <HStack spacing={2}>
                                    {isEditingInvoiceNumber && !isReadOnly ? (
                                        <Input
                                            ref={invoiceInputRef as React.RefObject<HTMLInputElement>}
                                            value={getStringValue('invoiceNumber')}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('invoiceNumber', e.target.value)}
                                            onBlur={(e) => {
                                                commitKeyIdentifierCorrection({
                                                    invoiceField: 'invoiceNumber',
                                                    teachField: 'invoiceNumber',
                                                    fieldLabel: 'Invoice number',
                                                    correctedValue: e.currentTarget.value,
                                                });
                                                setIsEditingInvoiceNumber(false);
                                            }}
                                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                if (e.key === 'Enter') {
                                                    e.currentTarget.blur();
                                                }
                                                if (e.key === 'Escape') setIsEditingInvoiceNumber(false);
                                            }}
                                            fontSize="sm"
                                            fontWeight="medium"
                                            border="none"
                                            outline="1.5px solid"
                                            outlineColor="blue.300"
                                            borderRadius="md"
                                            color="gray.900"
                                            bg="blue.50"
                                            _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                                            autoFocus
                                            w="180px"
                                            maxW="100%"
                                            p={0}
                                            px={1}
                                            h="auto"
                                            lineHeight="short"
                                        />
                                    ) : (
                                        <Text
                                            color="gray.900"
                                            fontSize="sm"
                                            cursor={isReadOnly ? 'default' : 'pointer'}
                                            _hover={isReadOnly ? undefined : { color: ACCENT }}
                                            onClick={() => {
                                                if (isReadOnly) return;
                                                setIsEditingInvoiceNumber(true);
                                            }}
                                            minW="120px"
                                        >
                                            {getStringValue('invoiceNumber') || <Text as="span" color="gray.400">Enter invoice number</Text>}
                                        </Text>
                                    )}
                                    {!isReadOnly && isFieldEdited('invoiceNumber') && (
                                        <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Compare"
                                                icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => openRevertModal('invoiceNumber', 'Invoice number')}
                                                _hover={{ bg: 'gray.100' }}
                                                minW="auto"
                                                h="auto"
                                                p={1}
                                            />
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Box>
                        </Box>

                        {currentInvoiceType === 'return_slip' && nexReconciliationIdentifierMode === 'ro' && (
                            <Box
                                flex={1}
                                role="group"
                                onMouseEnter={() =>
                                    onFieldImageHighlightChange?.(
                                        invoice.imageFieldHighlights?.purchaseOrderNumber ?? null,
                                    )
                                }
                                onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                            >
                                <KeyIdentifierFieldTitle
                                    label="PO # (NEX)"
                                    teachField="purchaseOrderNumber"
                                    teachableFields={teachableFields}
                                    onOpenExtractionTeaching={onOpenExtractionTeaching}
                                    onReportWrongValue={reportWrongValue(
                                        'purchaseOrderNumber',
                                        'purchaseOrderNumber',
                                        'PO # (NEX)',
                                        getStringValue('purchaseOrderNumber'),
                                    )}
                                />
                                <Box px={0} py={0}>
                                    <HStack spacing={2}>
                                        <Tooltip
                                            label="Supplier / NEX purchase order number from the return slip (e.g. next to “PO:”), not the shop folder. Used to pick the right NEX line when the same part exists on multiple POs."
                                            placement="top"
                                            hasArrow
                                            maxW="320px"
                                        >
                                            <Box flexShrink={0}>
                                                <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.400" cursor="help" />
                                            </Box>
                                        </Tooltip>
                                        <Editable
                                            value={getStringValue('purchaseOrderNumber')}
                                            onChange={(value) => updateField('purchaseOrderNumber', value)}
                                            submitOnBlur
                                            placeholder="e.g. 544189"
                                            isDisabled={isReadOnly}
                                            flex={1}
                                        >
                                            <EditablePreview w="100%" cursor="pointer" color="gray.900" fontSize="sm" py={0} px={0} fontFamily="mono" />
                                            <EditableInput
                                                {...editableInputCommitOnBlur({
                                                    invoiceField: 'purchaseOrderNumber',
                                                    teachField: 'purchaseOrderNumber',
                                                    fieldLabel: 'PO # (NEX)',
                                                })}
                                                border="none"
                                                outline="1.5px solid"
                                                outlineColor="blue.300"
                                                borderRadius="md"
                                                color="gray.900"
                                                fontSize="sm"
                                                bg="blue.50"
                                                py="2px"
                                                px={1}
                                                h="auto"
                                                lineHeight="short"
                                                fontFamily="mono"
                                                _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }}
                                            />
                                        </Editable>
                                        {!isReadOnly && isFieldEdited('purchaseOrderNumber') && (
                                            <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                                <IconButton
                                                    aria-label="Compare"
                                                    icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => openRevertModal('purchaseOrderNumber', 'PO number (NEX)')}
                                                    _hover={{ bg: 'gray.100' }}
                                                    minW="auto"
                                                    h="auto"
                                                    p={1}
                                                />
                                            </Tooltip>
                                        )}
                                    </HStack>
                                </Box>
                            </Box>
                        )}
                    </Flex>
                </Box>

                <Box p={4}>
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={4} align="stretch">
                        <Box
                            flex={1}
                            minW={0}
                            role="group"
                            onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.timestamp ?? null)}
                            onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                        >
                            <KeyIdentifierFieldTitle
                                label="Invoice Date"
                                teachField="invoiceDate"
                                teachableFields={teachableFields}
                                onOpenExtractionTeaching={onOpenExtractionTeaching}
                                onReportWrongValue={reportWrongValue(
                                    'timestamp',
                                    'invoiceDate',
                                    'Invoice date',
                                    formatInvoiceDateForKeyIds(getDateValue()),
                                )}
                            />
                            <HStack spacing={2} align="center" flexWrap="wrap">
                                {isReadOnly ? (
                                    <Text fontSize="sm" color="gray.900">
                                        {formatInvoiceDateForKeyIds(getDateValue())}
                                    </Text>
                                ) : (
                                    <Box w="180px" maxW="100%">
                                        <DatePicker
                                            value={getDateValue()}
                                            onChange={(d) => {
                                                updateField('timestamp', d);
                                                commitKeyIdentifierCorrection({
                                                    invoiceField: 'timestamp',
                                                    teachField: 'invoiceDate',
                                                    fieldLabel: 'Invoice date',
                                                    correctedValue: formatInvoiceDateForKeyIds(d),
                                                });
                                            }}
                                            size="sm"
                                            flex="1"
                                        />
                                    </Box>
                                )}
                                {!isReadOnly && isFieldEdited('timestamp') && (
                                    <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                        <IconButton
                                            aria-label="Compare"
                                            icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => openRevertModal('timestamp', 'Invoice date')}
                                            _hover={{ bg: 'gray.100' }}
                                            minW="auto"
                                            h="auto"
                                            p={1}
                                        />
                                    </Tooltip>
                                )}
                            </HStack>
                        </Box>
                        <Box
                            flex={1}
                            minW={0}
                            role="group"
                            onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.supplierText ?? null)}
                            onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                        >
                            <KeyIdentifierFieldTitle label="Vendor" />
                            <Box px={0} py={0} bg={vendorMismatch ? 'orange.50' : 'white'}>
                                <HStack spacing={2}>
                                    <Icon as={TbTruck as React.ElementType} boxSize={4} color="gray.600" />
                                    <Text fontSize="sm" color="gray.900" maxW="240px" noOfLines={1}>
                                        {keyIdentifiersVendorDisplay}
                                    </Text>
                                    {vendorMismatch && !isSubmittedOrManual ? (
                                        <Tooltip label="Choose vendor from NEX response" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Open vendor selector"
                                                icon={<Icon as={TbBulb as React.ElementType} boxSize={4} />}
                                                variant="ghost"
                                                size="xs"
                                                color="orange.500"
                                                _hover={{ bg: 'orange.100' }}
                                                minW="auto"
                                                h="auto"
                                                p={0}
                                                onClick={openVendorSelectorModal}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip label="View original invoice vendor and NEX options" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="View vendor mapping details"
                                                icon={<Icon as={TbLink as React.ElementType} boxSize={4} />}
                                                variant="ghost"
                                                size="xs"
                                                color="blue.500"
                                                _hover={{ bg: 'blue.50' }}
                                                minW="auto"
                                                h="auto"
                                                p={0}
                                                onClick={openVendorSelectorModal}
                                            />
                                        </Tooltip>
                                    )}
                                    {!isReadOnly && isFieldEdited('supplierText') && (
                                        <Tooltip label="Value changed – click to compare" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Compare"
                                                icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => openRevertModal('supplierText', 'Vendor')}
                                                _hover={{ bg: 'gray.100' }}
                                                minW="auto"
                                                h="auto"
                                                p={1}
                                            />
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Box>
                        </Box>
                    </Flex>
                </Box>
            </VStack>
        </Box>
    );
};

